// @todo: Migrate to 'Switch' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Switch} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {debounce} from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Input} from 'semantic-ui-react';

import {isLegendPosition} from '../util/plotHelpers/legend';
import {isFontSizeOrAuto} from '../util/plotHelpers/plotFontSize';
import {Query} from '../util/queryTypes';
import {LabeledOptionBlock as LabeledOption} from './elements/LabeledOptionBlock';
import MultiButton from './elements/MultiButton';
import {BarChartConfig} from './PanelBarChart/types';
import {RunsLinePlotConfig} from './PanelRunsLinePlot/types';

type PanelConfig = RunsLinePlotConfig | BarChartConfig;

interface PanelChartOptionsProps {
  config: PanelConfig;
  defaultTitle?: string;
  pageQuery: Query; // for the field selector
  showLegend?: boolean;
  type: 'lines' | 'bars' | 'scalar';
  updateConfig: (newConfig: PanelConfig) => void;
}

type DebouncedInputOptionProps = {
  label?: string;
  value?: string;
  update: (newValue: string) => void;
  placeholder?: string;
};

export function DebouncedInputOption({
  label,
  value = '',
  update,
  placeholder = '',
}: DebouncedInputOptionProps) {
  const [displayedValue, setDisplayedValue] = useState(value);
  const debouncedUpdate = useMemo(() => debounce(update, 200), [update]);
  // if value prop changes, sync to displayedValue state
  useEffect(() => {
    setDisplayedValue(value);
  }, [value]);
  const input = (
    <Input
      placeholder={placeholder}
      value={displayedValue}
      onChange={(e, {value: newValue}) => {
        setDisplayedValue(newValue);
        debouncedUpdate(newValue);
      }}
    />
  );
  return label ? <LabeledOption label={label} option={input} /> : input;
}

const fontSizeOptions = ['small', 'medium', 'large', 'auto'];
const fontSizeLabels = ['Small', 'Medium', 'Large', 'Auto'];

const legendPositionOptions = ['north', 'south', 'east', 'west'];
const legendPositionLabels = ['North', 'South', 'East', 'West'];

const PanelChartOptions = (props: PanelChartOptionsProps) => {
  const {config, showLegend, updateConfig} = props;

  return (
    <Tailwind>
      {(props.type === 'lines' || props.type === 'scalar') && (
        <LabeledOption
          label="Show legend"
          labelWidth="140px"
          option={
            <Switch.Root
              size="medium"
              checked={showLegend}
              onCheckedChange={isChecked =>
                updateConfig({showLegend: isChecked})
              }>
              <Switch.Thumb size="medium" checked={showLegend ?? false} />
            </Switch.Root>
          }
        />
      )}
      {(props.type === 'lines' || props.type === 'bars') && (
        <>
          <LabeledOption
            label="Font size"
            labelWidth="140px"
            option={
              <MultiButton
                keys={fontSizeOptions}
                labels={fontSizeLabels}
                selected={config.fontSize ?? 'auto'}
                onChange={key => {
                  if (isFontSizeOrAuto(key)) {
                    updateConfig({
                      fontSize: key,
                    });
                  }
                }}
              />
            }
          />
        </>
      )}
      {props.type === 'lines' && (
        <>
          <LabeledOption
            label="Legend position"
            helpText="Placement of the chart legend."
            labelWidth="140px"
            option={
              <MultiButton
                keys={legendPositionOptions}
                labels={legendPositionLabels}
                selected={
                  (config as RunsLinePlotConfig).legendPosition ?? 'north'
                }
                onChange={key => {
                  if (isLegendPosition(key)) {
                    updateConfig({legendPosition: key});
                  }
                }}
              />
            }
          />
        </>
      )}
    </Tailwind>
  );
};

export default PanelChartOptions;
